import React, { useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { useDispatch, useSelector } from 'react-redux'
import { Outlet, RouteProps, useLocation, useNavigate } from 'react-router-dom'
import { RootState, setCompanyRoles, setPermissions, setUnits } from 'store'
import { Layout } from 'layout'
import {
  resetProfileOnSignOut,
  updateCompany,
  updateProfile,
  setSettings,
} from 'store/slices/userSlice'
import { setValidationRules } from 'store/slices/validationsSlice'
import { TRule } from 'utils'
import { fetchUserProfile, useWheatCodesService } from '../../services'
import usePermissionsService from '../../services/permissionsService'
import { useSettingsService } from 'services/settingsService'

type ProtectedRoute = {
  isAuthenticated?: boolean
} & RouteProps

export const ProtectedRoute: React.FC<ProtectedRoute> = () => {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const location = useLocation()
  const queryClient = useQueryClient()
  const lastCompanyIdRef = useRef<number | null>(null)

  const { getUserPermissions } = usePermissionsService()
  const { getWheatCodeAttributes } = useWheatCodesService()
  const { getPublicSettings } = useSettingsService()

  const profile = useSelector((state: RootState) => state.user.profile)

  const { data, isLoading, isError } = useQuery(
    ['user-profile', profile?.email],
    fetchUserProfile,
    { retry: false, refetchOnWindowFocus: false }
  )

  const { data: permissionsData } = useQuery(
    ['user-permissions', profile?.email],
    getUserPermissions,
    { retry: false, refetchOnWindowFocus: false }
  )

  const { data: settingsData } = useQuery(
    ['public-settings'],
    () => getPublicSettings(),
    { retry: false, refetchOnWindowFocus: false }
  )

  const { data: defaultAttributes } = useQuery({
    queryKey: ['wheat_codes_attributes'],
    queryFn: () => getWheatCodeAttributes(),
  })

  useEffect(() => {
    const currentCompanyId = data?.profile?.company?.id

    if (data?.redirect_to_tou_page && data?.current_active_tou_version_id) {
      navigate('auth/terms-of-use')
      return
    }
    if (data?.profile?.email && currentCompanyId) {
      const previousCompanyId = lastCompanyIdRef.current

      if (
        previousCompanyId !== null &&
        previousCompanyId !== currentCompanyId
      ) {
        queryClient.clear()
      }

      lastCompanyIdRef.current = currentCompanyId

      dispatch(resetProfileOnSignOut())
      dispatch(updateProfile({ ...data.profile }))
      dispatch(updateCompany(data.profile?.company))
      dispatch(setCompanyRoles(data.profile?.company_roles))
    }
  }, [data, dispatch, queryClient])

  useEffect(() => {
    if (permissionsData?.result) {
      dispatch(setPermissions(permissionsData.result))
    }
  }, [permissionsData, dispatch])

  useEffect(() => {
    const flatSettings: Record<string, boolean | string | number> = {}

    settingsData?.forEach((setting) => {
      let parsedValue: boolean | string | number = setting.value
      switch (setting.value_type) {
        case 'Boolean':
          parsedValue = setting.value === 'true'
          break
        case 'Number':
          parsedValue = Number(setting.value)
          break
        default:
          parsedValue = setting.value
      }

      flatSettings[setting.key] = parsedValue
    })
    dispatch(setSettings(flatSettings))
  }, [settingsData, data?.profile?.email, dispatch])

  useEffect(() => {
    if (defaultAttributes?.results) {
      const rules = defaultAttributes.results.reduce(
        (acc, attribute) => {
          acc[attribute.name] = {
            min: attribute.min_value,
            max: attribute.max_value,
            warnAbove: attribute.warn_above,
            hardMax: attribute.hard_max,
            warnBelow: attribute.warn_below,
            hardMin: attribute.hard_min,
          } as TRule
          return acc
        },
        {} as Record<string, TRule>
      )

      const unitsMapping = defaultAttributes.results.reduce(
        (acc, attribute) => {
          const unit = attribute.unit ?? ''
          acc[attribute.name] = unit ? ` ${unit}` : ''
          return acc
        },
        {} as Record<string, string>
      )

      dispatch(setUnits(unitsMapping))
      dispatch(setValidationRules(rules))
    }
  }, [defaultAttributes, dispatch])

  useEffect(() => {
    if (!profile?.email && !isLoading && isError) {
      localStorage.removeItem('token')
      dispatch(resetProfileOnSignOut())
      navigate('/auth/login')
    }
  }, [
    isError,
    isLoading,
    profile?.email,
    navigate,
    location.pathname,
    dispatch,
  ])

  return profile?.email ? (
    <Layout>
      <Outlet />
    </Layout>
  ) : null
}
